/* scene.js — PixiJS v8 port of the futari-jinja 2.5D shrine grounds.
 *
 * World coordinates are IDENTICAL to the original SVG user space:
 *   ground line y = 796, torii pillars at x 72/486, saisen box at x 300,
 *   ema rack around x 130, 社務所 hut around x 680, walkable x -30..770.
 * The framing rules (SAFE_W/SAFE_H/TOP_Y/CX) are copied from the original
 * `fit()` so the composition lands in exactly the same place on any screen.
 *
 * Engine notes (decided, do not relitigate):
 *   - WebGLRenderer explicitly. WebGPU is unstable on Safari.
 *   - antialias:false (pixijs#10413 perf bug).
 *   - resolution capped at min(2, devicePixelRatio).
 *   - Rain is a ParticleContainer.
 *   - Static groups are cacheAsTexture'd, so per-frame work is transforms only.
 */

import {
  WebGLRenderer, Container, Graphics, Sprite, TilingSprite, BlurFilter,
  ParticleContainer, Particle, Texture,
} from 'pixi.js';

import {
  P, fillPath, strokePath,
  linearGradTex, radialGradTex, ringTex, streakTex, noiseTex,
  gradRect, gradPath, svgText, SERIF, SANS,
  easeOut, kf, pivotAt,
} from './gfx.js';

/* ============================ framing constants ============================ */

const SAFE_W = 572, SAFE_H = 856, TOP_Y = 56, CX = 300;
const GROUND_Y = 796;          // the line the avatars' feet sit on
const WALK_MIN = -30, WALK_MAX = 770;

/* ============================ gradients (defs) ============================ */

const TEX = {
  sky: () => linearGradTex('sky', [
    [0, 0x03050a], [0.42, 0x080e16], [0.74, 0x111820], [1, 0x1b1f1e],
  ]),
  warm: () => radialGradTex('warm', [
    [0, 0xff9c3c, 0.34], [0.55, 0xc25e1c, 0.12], [1, 0x000000, 0],
  ], 0.5, 0.6, 0.55),
  lampG: () => radialGradTex('lampG', [
    [0, 0xffd694, 0.8], [0.4, 0xff9c34, 0.3], [1, 0xff8a20, 0],
  ], 0.5, 0.5, 0.5),
  wood: () => linearGradTex('wood', [
    [0, 0x3a0c08], [0.18, 0x8c2216], [0.44, 0xad3120], [0.74, 0x761810], [1, 0x2b0705],
  ], true),
  woodH: () => linearGradTex('woodH', [
    [0, 0xbd3a26], [0.3, 0x8f2110], [0.72, 0x61140c], [1, 0x250705],
  ]),
  ground: () => linearGradTex('ground', [
    [0, 0x191a17], [0.3, 0x222320], [1, 0x0a0b09],
  ]),
  hideVP: () => linearGradTex('hideVP', [
    [0, 0x101210, 0.98], [1, 0x101210, 0],
  ]),
  boxwood: () => linearGradTex('boxwood', [
    [0, 0x5a4028], [0.35, 0x3d2a19], [1, 0x1c1208],
  ]),
  goldG: () => linearGradTex('goldG', [
    [0, 0xf6e0a6], [0.42, 0xcba54e], [1, 0x6b4c19],
  ]),
  suzuG2: () => radialGradTex('suzuG2', [
    [0, 0xffeebb], [0.35, 0xe0bb64], [0.72, 0xa67f30], [1, 0x4e3812],
  ], 0.36, 0.3, 0.78),
  omi: () => linearGradTex('omi', [
    [0, 0x2a1a0d], [0.3, 0x6e4c27], [0.62, 0x4a3117], [1, 0x1e120a],
  ], true),
  roofG: () => linearGradTex('roofG', [
    [0, 0x252a30], [0.55, 0x171b20], [1, 0x0d1013],
  ]),
};

const GOLD_FLAT = 0xc9a24c;    // stand-in where the original strokes with url(#goldG)

/* ============================ shape builders ============================ */

/** the original's pine() — 5-tier conifer silhouette */
function pinePath(cx, base, h, w) {
  let p = 'M' + cx + ' ' + (base - h) + ' ';
  const tiers = 5;
  for (let i = 1; i <= tiers; i++) {
    const yy = base - h + (h * i / tiers), ww = w * (i / tiers);
    p += 'L' + (cx + ww * 0.55) + ' ' + yy + ' L' + (cx + ww * 0.3) + ' ' + (yy - h * 0.045) + ' ';
  }
  p += 'L' + (cx + w * 0.16) + ' ' + base + ' L' + (cx - w * 0.16) + ' ' + base + ' ';
  for (let j = tiers; j >= 1; j--) {
    const yy2 = base - h + (h * j / tiers), ww2 = w * (j / tiers);
    p += 'L' + (cx - ww2 * 0.3) + ' ' + (yy2 - h * 0.045) + ' L' + (cx - ww2 * 0.55) + ' ' + yy2 + ' ';
  }
  return p + 'Z';
}

function buildForest() {
  const c = new Container();
  c.alpha = 0.95;
  const g = new Graphics();
  fillPath(g, pinePath(-120, 632, 150, 150) + pinePath(560, 632, 150, 150), 0x05080a);
  fillPath(g, pinePath(-30, 634, 120, 120), 0x05080a);
  fillPath(g, pinePath(650, 634, 124, 124), 0x05080a);
  fillPath(g, pinePath(40, 636, 96, 96) + pinePath(720, 636, 100, 100), 0x060a0c);
  fillPath(g, pinePath(96, 630, 78, 78) + pinePath(506, 630, 82, 84), 0x070b0d);
  c.addChild(g);
  return c;
}

function buildHall() {
  const c = new Container();
  const g = new Graphics();
  g.rect(150, 470, 300, 184).fill(0x0e0b08);
  g.rect(174, 492, 252, 132).fill(0x1a1109);
  g.rect(194, 500, 212, 114).fill({ color: 0xff9b3a, alpha: 0.12 });
  g.rect(214, 514, 172, 90).fill({ color: 0xffbe62, alpha: 0.09 });
  g.rect(174, 480, 15, 174).fill(0x28100a);
  g.rect(411, 480, 15, 174).fill(0x28100a);
  g.rect(250, 490, 10, 164).fill({ color: 0x1e0f09, alpha: 0.85 });
  g.rect(340, 490, 10, 164).fill({ color: 0x1e0f09, alpha: 0.85 });
  c.addChild(g);

  // roof mass — shallow hip roof
  c.addChild(gradPath(TEX.roofG(), 'M300 404 L488 484 L504 502 L96 502 L112 484 Z'));

  const g2 = new Graphics();
  strokePath(g2, 'M231 434 H369 M198 448 H402 M164 462 H436 M130 476 H470',
    { color: 0x000000, alpha: 0.3, width: 1.4 });
  strokePath(g2, 'M231 436 H369 M198 450 H402 M164 464 H436 M130 478 H470',
    { color: 0x39424c, alpha: 0.4, width: 1 });
  // eave: shallow sag, upturned corners
  fillPath(g2, 'M94 500 Q300 514 506 500 L510 514 Q300 530 90 514 Z', 0x090c0f);
  strokePath(g2, 'M94 500 Q300 514 506 500', { color: GOLD_FLAT, alpha: 0.22, width: 1.6 });
  fillPath(g2, 'M90 514 l-14 -8 l6 -5 l12 7 z M510 514 l14 -8 l-6 -5 l-12 7 z', 0x090c0f);
  // ridge beam
  g2.roundRect(252, 396, 96, 13, 3).fill(0x232930);
  g2.rect(252, 396, 96, 3).fill({ color: 0x3d4650, alpha: 0.7 });
  fillPath(g2, 'M254 398 l-9 -14 l7 -2 l7 12 z M346 398 l9 -14 l-7 -2 l-7 12 z', 0x232930);
  // steps
  fillPath(g2, 'M138 654 h324 l14 16 h-352 z', 0x26251f);
  fillPath(g2, 'M124 670 h352 l16 18 h-384 z', 0x1c1b16);
  c.addChild(g2);
  return c;
}

/** one hanging lantern; `dx` shifts the whole thing off the x=182 original */
function buildLantern(dx) {
  const g = new Graphics();
  const X = (v) => v + dx;
  g.rect(X(180), 502, 4, 16).fill(0x2a1109);
  g.ellipse(X(182), 522, 21, 7).fill(0x241009);
  fillPath(g, `M${X(161)} 524 q-6 20 0 40 q21 9 42 0 q6-20 0-40 q-21-8-42 0z`, 0xf2dcaa);
  fillPath(g, `M${X(161)} 524 q-6 20 0 40 q21 9 42 0 q6-20 0-40 q-21-8-42 0z`, 0xff9d3a, 0.5);
  strokePath(g,
    `M${X(160)} 534 q22 7 44 0 M${X(159)} 544 q23 7 46 0 M${X(160)} 554 q22 7 44 0`,
    { color: 0xb87a34, alpha: 0.5, width: 0.8 });
  strokePath(g, `M${X(172)} 532 q10 22 0 30`, { color: 0xa01f16, alpha: 0.6, width: 5 });
  g.ellipse(X(182), 566, 18, 6).fill(0x241009);

  const c = new Container();
  const glow = new Sprite(TEX.lampG());
  glow.anchor.set(0.5);
  glow.position.set(X(182), 546);
  glow.width = glow.height = 148;
  c.addChild(glow, g);
  return c;
}

function buildTorii() {
  const c = new Container();
  // pillars
  c.addChild(gradPath(TEX.wood(), 'M72 214 l42 0 l9 556 l-60 0 z'));
  c.addChild(gradPath(TEX.wood(), 'M486 214 l42 0 l9 556 l-60 0 z'));

  const g = new Graphics();
  g.ellipse(93, 772, 36, 9).fill(0x150504);
  g.ellipse(507, 772, 36, 9).fill(0x150504);
  c.addChild(g);

  c.addChild(gradRect(TEX.woodH(), 40, 284, 520, 32));

  const g2 = new Graphics();
  g2.rect(40, 284, 520, 5).fill({ color: 0xd8603f, alpha: 0.45 });
  g2.rect(275, 216, 50, 68).fill(0x150604);
  g2.rect(279, 220, 42, 60).stroke({ color: GOLD_FLAT, width: 2 });
  c.addChild(g2);

  c.addChild(svgText('神', 300, 243, 18, 0xd8b463));
  c.addChild(svgText('社', 300, 270, 18, 0xd8b463));

  const g3 = new Graphics();
  fillPath(g3, 'M12 194 q288-26 576 0 l0 22 q-288-26-576 0 z', 0x6a140c);
  c.addChild(g3);

  c.addChild(gradPath(TEX.woodH(), 'M0 150 q300-34 600 0 l6 30 q-306-34-612 0 z'));

  const g4 = new Graphics();
  fillPath(g4, 'M0 150 q300-34 600 0 l2 9 q-302-34-604 0 z', 0xd8603f, 0.4);
  // warm rim light down the inside edge of each pillar
  fillPath(g4, 'M72 214 l8 0 l6 556 l-10 0 z M486 214 l8 0 l6 556 l-10 0 z', 0xff9d55, 0.14);
  c.addChild(g4);
  return c;
}

/** folded paper streamer — wide bars offset alternately */
function shide(g, x, y) {
  const W = 23, N = 11, H = 11;
  const bars = [[0, 0, W], [N + 1, H, N - 1], [0, H * 2, W], [0, H * 3, N - 1], [0, H * 4, W], [N + 1, H * 5, N - 1]];
  for (let i = 0; i < bars.length; i++) {
    g.rect(x + bars[i][0], y + bars[i][1], bars[i][2], H + 0.6).fill(i % 2 ? 0xddd8c9 : 0xf1ece0);
  }
}

function buildShimenawa() {
  const g = new Graphics();
  fillPath(g, 'M86 320 q214 66 428 0 l0 15 q-214 68-428 0 z', 0x8a7c58);
  fillPath(g, 'M86 320 q214 66 428 0 l0 5 q-214 68-428 0 z', 0xb3a377, 0.8);
  fillPath(g, 'M86 330 q214 68 428 0 l0 6 q-214 68-428 0 z', 0x000000, 0.28);
  strokePath(g,
    'M120 333 q12 14 26 17 M186 351 q12 13 26 15 M256 361 q12 12 26 13 ' +
    'M326 361 q12 12 26 12 M394 351 q12 12 26 11 M456 333 q12 12 26 10',
    { color: 0x6d6043, width: 2.2, alpha: 0.65 });
  shide(g, 142, 348); shide(g, 232, 366); shide(g, 346, 366); shide(g, 436, 348);
  return g;
}

function buildGround() {
  const root = new Container();
  const mask = new Graphics().rect(-900, 612, 2400, 760).fill(0xffffff);
  root.addChild(mask);
  root.mask = mask;

  root.addChild(gradRect(TEX.ground(), -900, 612, 2400, 760));

  const grid = new Graphics();
  strokePath(grid,
    'M-900 664 H1500 M-900 704 H1500 M-900 756 H1500 M-900 824 H1500 ' +
    'M-900 910 H1500 M-900 1016 H1500 M-900 1146 H1500 ' +
    'M300 612 L-420 1400 M300 612 L-40 1400 M300 612 L180 1400 ' +
    'M300 612 L420 1400 M300 612 L640 1400 M300 612 L1020 1400',
    { color: 0x3c3c33, alpha: 0.2, width: 1.4 });
  root.addChild(grid);

  root.addChild(gradRect(TEX.hideVP(), -900, 612, 2400, 96));

  /* --- wet-ground reflections. filter=url(#blur6) on the torii/lantern
     smears, filter=url(#blur16) on the broad warm pool. The blur is baked
     once into a cached texture, so there is no per-frame filter pass. --- */
  const refl = new Container();
  refl.alpha = 0.55;
  const blurred = new Container();
  blurred.alpha = 0.6;
  const rg = new Graphics();
  fillPath(rg, 'M72 776 l42 0 l9 150 l-60 0 z M486 776 l42 0 l9 150 l-60 0 z', 0xa02a1a);
  rg.ellipse(182, 712, 20, 52).fill({ color: 0xff9d3a, alpha: 0.5 });
  rg.ellipse(418, 712, 20, 52).fill({ color: 0xff9d3a, alpha: 0.5 });
  rg.ellipse(300, 726, 17, 58).fill({ color: 0xe8c070, alpha: 0.38 });
  blurred.addChild(rg);
  blurred.filters = [new BlurFilter({ strength: 6, quality: 4 })];
  refl.addChild(blurred);

  const pool = new Container();
  const pg = new Graphics().ellipse(300, 760, 210, 80).fill({ color: 0xff9b3a, alpha: 0.14 });
  pool.addChild(pg);
  pool.filters = [new BlurFilter({ strength: 16, quality: 4 })];
  refl.addChild(pool);
  root.addChild(refl);

  // puddles
  const pud = new Graphics();
  pud.alpha = 0.32;
  pud.ellipse(150, 884, 118, 20).fill(0x31322a);
  pud.ellipse(150, 884, 118, 20).fill({ color: 0xffb45e, alpha: 0.07 });
  pud.ellipse(474, 830, 96, 15).fill(0x31322a);
  pud.ellipse(474, 830, 96, 15).fill({ color: 0xffb45e, alpha: 0.06 });
  pud.ellipse(300, 1012, 250, 34).fill(0x2f3028);
  pud.ellipse(300, 1012, 250, 34).fill({ color: 0xffb45e, alpha: 0.05 });
  root.addChild(pud);

  const ripples = new Container();
  root.addChild(ripples);

  return { root, refl, ripples, blurred, pool };
}

function buildBell() {
  const unit = new Container();

  const glow = new Sprite(TEX.lampG());
  glow.anchor.set(0.5); glow.position.set(300, 532);
  glow.width = glow.height = 132; glow.alpha = 0.5;
  unit.addChild(glow);

  // --- suzu (bell), swings about its top ---
  const suzu = pivotAt(300, 486);
  const sg = new Graphics();
  sg.rect(297, 486, 6, 12).fill(0x3a2a12);
  suzu.inner.addChild(sg);
  suzu.inner.addChild(gradPath(TEX.goldG(), 'M289 496 q11-6 22 0 l3 7 q-14-5-28 0 z'));
  suzu.inner.addChild(gradPath(TEX.suzuG2(),
    'M300 500 c-23 0 -41 16 -41 36 c0 21 18 36 41 36 s41-15 41-36 c0-20-18-36-41-36 z'));
  const sg2 = new Graphics();
  fillPath(sg2, 'M263 546 q37 13 74 0 l0 8 q-37 13-74 0 z', 0x3b2a10, 0.92);
  fillPath(sg2, 'M263 546 q37 13 74 0 l0 3 q-37 13-74 0 z', 0x000000, 0.5);
  strokePath(sg2, 'M280 507 q-7 20 0 39 M320 507 q7 20 0 39',
    { color: 0x7d5c22, alpha: 0.5, width: 1.4 });
  suzu.inner.addChild(sg2);
  const spec = new Graphics().ellipse(0, 0, 11, 8).fill({ color: 0xfff6d6, alpha: 0.4 });
  spec.position.set(285, 518); spec.rotation = -28 * Math.PI / 180;
  suzu.inner.addChild(spec);
  const sg3 = new Graphics();
  strokePath(sg3, 'M259 536 q41 22 82 0', { color: 0xfff0c8, alpha: 0.16, width: 2 });
  sg3.ellipse(300, 570, 26, 6).fill({ color: 0x3d2c10, alpha: 0.8 });
  suzu.inner.addChild(sg3);
  unit.addChild(suzu);

  // --- rope (red/white braid), swings about its top ---
  const rope = pivotAt(300, 568);
  const top = 568, bot = 700, n = 11, h = (bot - top) / n;
  const band = new Container();
  const clip = new Graphics()
    .path(P(`M287 ${top} L313 ${top} L320 ${bot} L280 ${bot} Z`)).fill(0xffffff);
  const body = new Graphics();
  body.rect(276, top, 48, bot - top).fill(0xefe7d4);
  for (let i = 0; i < n; i++) {
    const y = top + i * h;
    if (i % 2 === 0) {
      fillPath(body,
        `M276 ${y} L324 ${y} L324 ${y + h * 0.62} Q300 ${y + h * 1.02} 276 ${y + h * 0.62} Z`,
        0xc23a2c);
    }
  }
  fillPath(body, `M276 ${top} L288 ${top} L292 ${bot} L276 ${bot} Z`, 0x000000, 0.22);
  fillPath(body, `M312 ${top} L324 ${top} L324 ${bot} L308 ${bot} Z`, 0x000000, 0.28);
  fillPath(body, `M296 ${top} L303 ${top} L305 ${bot} L297 ${bot} Z`, 0xffffff, 0.14);
  band.addChild(body, clip);
  body.mask = clip;
  rope.inner.addChild(band);

  const fusa = new Graphics();
  fillPath(fusa, 'M277 696 q23 11 46 0 l1 17 q-24 11-48 0 z', 0xa02418);
  fillPath(fusa, 'M277 696 q23 11 46 0 l1 5 q-24 11-48 0 z', 0xc9403a, 0.8);
  strokePath(fusa,
    'M283 712 l-3 20 M291 715 l-1 22 M300 716 l0 23 M309 715 l1 22 M317 712 l3 20',
    { color: 0xded5bb, width: 3.4, cap: 'round', alpha: 0.85 });
  rope.inner.addChild(fusa);
  unit.addChild(rope);

  return { unit, suzu, rope };
}

function buildSaisenBox() {
  const c = new Container();
  const g = new Graphics();
  g.ellipse(300, 830, 122, 15).fill({ color: 0x000000, alpha: 0.6 });
  fillPath(g, 'M180 738 l240 0 l14 -15 l-268 0 z', 0x2d1f12);
  c.addChild(g);

  c.addChild(gradRect(TEX.boxwood(), 180, 738, 240, 88));

  const g2 = new Graphics();
  fillPath(g2, 'M420 738 l14 -15 l0 84 l-14 19 z', 0x241809);
  g2.rect(194, 727, 226, 17).fill(0x110b06);
  for (let x = 198; x <= 396; x += 22) g2.roundRect(x, 723, 7, 19, 2).fill(0x4c3b25);
  c.addChild(g2);

  c.addChild(gradRect(TEX.goldG(), 180, 738, 240, 4, 0.5));
  c.addChild(gradRect(TEX.goldG(), 180, 816, 240, 6, 0.35));
  c.addChild(gradRect(TEX.goldG(), 184, 746, 10, 72, 0.3));
  c.addChild(gradRect(TEX.goldG(), 406, 746, 10, 72, 0.3));

  const g3 = new Graphics();
  g3.rect(256, 758, 88, 48).stroke({ color: GOLD_FLAT, alpha: 0.55, width: 2 });
  c.addChild(g3);
  c.addChild(svgText('賽', 300, 777, 16, 0xe2c179));
  c.addChild(svgText('銭', 300, 799, 16, 0xe2c179));

  // warm rim light from the lanterns
  const g4 = new Graphics();
  fillPath(g4, 'M180 738 l240 0 l0 5 l-240 0 z', 0xffb45e, 0.3);
  fillPath(g4, 'M194 727 l226 0 l0 3 l-226 0 z', 0xffc987, 0.22);
  fillPath(g4, 'M180 738 l7 0 l0 88 l-7 0 z', 0xffb45e, 0.18);
  fillPath(g4, 'M413 738 l7 0 l0 88 l-7 0 z', 0xffb45e, 0.14);
  c.addChild(g4);
  return c;
}

function buildOmikuji() {
  /* root = [back, stick, front]. `stick` slides independently, so the two
     static halves are cached separately rather than the whole group. */
  const c = new Container();
  const back = new Container();
  const front = new Container();

  const g = new Graphics();
  g.ellipse(300, 842, 78, 13).fill({ color: 0x000000, alpha: 0.65 });
  back.addChild(g);

  // the numbered stick nests BEHIND the body
  const stick = new Container();
  const sg = new Graphics();
  sg.roundRect(291, 536, 18, 140, 4).fill(0xc9a97a);
  sg.roundRect(291, 536, 6, 140, 3).fill(0xe3cb9f);
  sg.roundRect(303, 536, 6, 140, 3).fill(0xa98a5c);
  sg.roundRect(291, 536, 18, 14, 4).fill(0xa01f16);
  stick.addChild(sg);
  stick.addChild(svgText('三', 300, 576, 12, 0x3a2a12));
  stick.addChild(svgText('十', 300, 592, 12, 0x3a2a12));
  stick.addChild(svgText('七', 300, 608, 12, 0x3a2a12));
  stick.addChild(svgText('番', 300, 627, 12, 0xa01f16));
  stick.y = 158;

  const g2 = new Graphics();
  fillPath(g2, 'M240 660 l120 0 l16 26 l-152 0 z', 0x3a2717);
  g2.ellipse(300, 672, 10, 4.5).fill(0x0a0603);
  front.addChild(g2);

  front.addChild(gradPath(TEX.omi(), 'M238 686 l124 0 l-11 156 l-102 0 z'));

  const g3 = new Graphics();
  fillPath(g3, 'M238 686 l124 0 l-3 10 l-118 0 z', 0x815b32);
  fillPath(g3, 'M249 702 l102 0 l-2 15 l-98 0 z', 0x1a0f07, 0.45);
  g3.rect(254, 730, 92, 46).stroke({ color: GOLD_FLAT, alpha: 0.5, width: 1.6 });
  front.addChild(g3);
  front.addChild(svgText('御', 300, 750, 16, 0xd8b463));
  front.addChild(svgText('籤', 300, 770, 16, 0xd8b463));
  const g4 = new Graphics();
  fillPath(g4, 'M244 794 l112 0 l-1 9 l-110 0 z', 0xc9a24c, 0.28);
  front.addChild(g4);

  c.addChild(back, stick, front);
  return { root: c, stick, back, front };
}

function buildRack() {
  const c = new Container();
  const g = new Graphics();
  g.rect(34, 700, 11, 112).fill(0x2b2418);
  g.rect(216, 700, 11, 112).fill(0x2b2418);
  g.roundRect(28, 698, 205, 10, 5).fill(0x4a3d26);
  g.roundRect(28, 698, 205, 3, 2).fill({ color: 0x6a5836, alpha: 0.7 });
  const xs = [52, 72, 92, 112, 132, 152, 172, 196];
  for (let i = 0; i < xs.length; i++) {
    const x = xs[i], h = 38 + ((i * 7) % 16);
    strokePath(g, `M${x} 704 q4 7 0 14`, { color: 0xcfc9b6, width: 2.6, alpha: 0.8 });
    fillPath(g, `M${x - 4.5} 715 l10 0 l-1 ${h} q-4 5-8 0 z`, 0xe7dfcb, 0.82);
    g.circle(x, 712, 3.2).fill({ color: 0xd8d1bd, alpha: 0.85 });
  }
  c.addChild(g);
  return c;
}

/** 社務所 — the office hut on the right of the grounds */
function buildHut() {
  /* root = [back, lantern, front] so the swaying lantern is never inside a
     cached container (caching would freeze it). */
  const root = new Container();
  const c = new Container();          // "back": everything behind the lantern
  const g = new Graphics();
  g.ellipse(678, 836, 95, 12).fill({ color: 0x000000, alpha: 0.5 });
  fillPath(g, 'M790 800 L820 716 L850 800 Z', 0x060a0c);
  fillPath(g, 'M758 806 L781 738 L804 806 Z', 0x05080a);
  g.rect(608, 648, 140, 160).fill(0x150e08);
  g.rect(620, 660, 80, 120).fill(0x1a1109);
  g.rect(628, 668, 64, 70).fill({ color: 0xff9b3a, alpha: 0.14 });
  g.rect(636, 676, 48, 54).fill({ color: 0xffbe62, alpha: 0.1 });
  strokePath(g, 'M652 668 V738 M628 700 H692', { color: 0x0d0805, width: 3 });
  g.rect(706, 676, 36, 132).fill(0x0c0703);
  g.rect(704, 676, 40, 8).fill(0x3a2717);
  fillPath(g, 'M704 684 h11 v30 h-11 z M733 684 h11 v30 h-11 z', 0x5c1610);
  fillPath(g, 'M718 684 h12 v25 h-12 z', 0x5c1610, 0.92);
  g.rect(604, 644, 12, 168).fill(0x28100a);
  g.rect(744, 644, 12, 168).fill(0x28100a);
  c.addChild(g);

  c.addChild(gradPath(TEX.roofG(), 'M588 612 L772 612 L786 652 L574 652 Z'));

  const g2 = new Graphics();
  fillPath(g2, 'M574 652 Q680 664 786 652 l3 10 Q680 676 571 662 z', 0x090c0f);
  strokePath(g2, 'M574 652 Q680 664 786 652', { color: GOLD_FLAT, alpha: 0.2, width: 1.4 });
  g2.roundRect(640, 600, 80, 12, 3).fill(0x232930);
  g2.rect(646, 622, 68, 20).fill(0x150604);
  g2.rect(649, 625, 62, 14).stroke({ color: GOLD_FLAT, width: 1.5 });
  c.addChild(g2);
  c.addChild(svgText('社務所', 680, 636, 10, 0xd8b463, { letterSpacing: 3 }));

  // hut lantern (sways + flickers like the pair on the haiden)
  const sway = pivotAt(614, 654);
  const flick = new Container();
  const lg = new Graphics();
  lg.rect(614 - 1.5, 668 - 8, 3, 8).fill(0x2a1109);
  fillPath(lg, 'M605 668 q-3 11 0 22 q9 4 18 0 q3-11 0-22 q-9-4-18 0z', 0xf2dcaa);
  fillPath(lg, 'M605 668 q-3 11 0 22 q9 4 18 0 q3-11 0-22 q-9-4-18 0z', 0xff9d3a, 0.5);
  const glow = new Sprite(TEX.lampG());
  glow.anchor.set(0.5); glow.position.set(614, 682);
  glow.width = glow.height = 56; glow.alpha = 0.75;
  flick.addChild(glow, lg);
  sway.inner.addChild(flick);

  const front = new Container();
  const g3 = new Graphics();
  fillPath(g3, 'M640 808 h110 l8 10 h-126 z', 0x26251f);
  fillPath(g3, 'M632 818 h126 l8 10 h-142 z', 0x1c1b16);
  front.addChild(g3);

  root.addChild(c, sway, front);
  return { root, back: c, front, sway, flick };
}

/** the three gold floor markers you can walk to */
function buildMarks() {
  const root = new Container();
  const pulses = [];
  const spec = [
    [300, 884, 26, 7, 'お参り'],
    [130, 868, 24, 6.5, '絵馬掛'],
    [688, 866, 26, 7, '社務所'],
  ];
  for (const [x, y, rx, ry, label] of spec) {
    const m = new Container();
    m.position.set(x, y);
    const base = new Graphics().ellipse(0, 0, rx, ry)
      .stroke({ color: 0xd8b463, alpha: 0.32, width: 1.2 });
    const pulse = new Graphics().ellipse(0, 0, rx, ry)
      .stroke({ color: 0xd8b463, alpha: 0.32, width: 1.2 });
    m.addChild(base, pulse);
    m.addChild(svgText(label, 0, -12, 11, 0xd8b463, { letterSpacing: 2, alpha: 0.7 }));
    root.addChild(m);
    pulses.push(pulse);
  }
  return { root, pulses };
}

/* ============================ avatars ============================ */

const PEOPLE = {
  D: { jp: 'デイヴィダス', color: 0xd8b463 },
  Y: { jp: 'Yuri', color: 0xf2b3c9 },
};

function buildAvatarBody(initial) {
  const g = new Graphics();
  if (initial === 'Y') {
    // a small hamster with a pink ribbon, holding a vermilion umbrella
    strokePath(g, 'M7 -92 L7 -4', { color: 0x3a2a12, width: 1.8 });
    fillPath(g, 'M-25 -80 A29 29 0 0 1 39 -80 Z', 0x8c2216);
    strokePath(g, 'M-25 -80 A29 29 0 0 1 39 -80', { color: 0xffb45e, alpha: 0.3, width: 1 });
    g.ellipse(0, -22, 17, 21).fill(0xe2b58a);
    g.ellipse(0, -15, 11, 13).fill(0xf6e7d2);
    g.circle(-9, -59, 4.6).fill(0xe2b58a);
    g.circle(-9, -59, 2.3).fill(0xf0a0b8);
    g.circle(9, -59, 4.6).fill(0xe2b58a);
    g.circle(9, -59, 2.3).fill(0xf0a0b8);
    g.circle(0, -47, 13.5).fill(0xe2b58a);
    g.ellipse(-6, -42.5, 5.6, 4.6).fill(0xf6e7d2);
    g.ellipse(6, -42.5, 5.6, 4.6).fill(0xf6e7d2);
    g.circle(-4.6, -48, 1.7).fill(0x2a1a12);
    g.circle(4.6, -48, 1.7).fill(0x2a1a12);
    g.ellipse(0, -44, 1.5, 1.1).fill(0xc76a6a);
    g.ellipse(-7, -3, 4.2, 3).fill(0xd8a87c);
    g.ellipse(7, -3, 4.2, 3).fill(0xd8a87c);

    const bow = new Graphics();
    fillPath(bow, 'M0 0 L-10 -6 L-8 4 Z M0 0 L10 -6 L8 4 Z', 0xf07ca0);
    bow.circle(0, 0, 2.6).fill(0xe05585);
    bow.position.set(0, -60);
    bow.rotation = -8 * Math.PI / 180;
    const c = new Container();
    c.addChild(g, bow);
    return c;
  }
  // indigo kimono, black hair, umbrella
  strokePath(g, 'M7 -96 L7 -4', { color: 0x3a2a12, width: 1.8 });
  fillPath(g, 'M-25 -84 A29 29 0 0 1 39 -84 Z', 0x2c3f66);
  strokePath(g, 'M-25 -84 A29 29 0 0 1 39 -84', { color: 0xffb45e, alpha: 0.3, width: 1 });
  fillPath(g, 'M-14 0 Q-12 -44 0 -49 Q12 -44 14 0 Z', 0x33507c);
  g.roundRect(-12, -30, 24, 6, 1).fill({ color: 0xc9a24c, alpha: 0.92 });
  g.circle(0, -56, 9.5).fill(0xe8c49a);
  fillPath(g, 'M-9.5 -56 a9.5 9.5 0 0 1 19 0 l0 -5 a9.5 8 0 0 0 -19 0 z', 0x1a130d);
  const c = new Container();
  c.addChild(g);
  return c;
}

function buildAvatar(initial) {
  const person = PEOPLE[initial] || PEOPLE.D;
  const root = new Container();

  const shadow = new Graphics().ellipse(0, 1, 22, 5.5).fill({ color: 0x000000, alpha: 0.5 });
  root.addChild(shadow);

  const flip = new Container();        // mirrored when facing right
  const bob = new Container();         // walk bounce
  const body = buildAvatarBody(initial);
  bob.addChild(body);
  flip.addChild(bob);
  root.addChild(flip);

  const dots = svgText('✎ …', 0, -106, 12, 0xf2e6cc, { family: SANS });
  dots.visible = false;
  root.addChild(dots);

  const name = svgText(person.jp, 0, 17, 11, person.color, { letterSpacing: 2 });
  root.addChild(name);

  root.y = GROUND_Y;
  return { root, flip, bob, body, dots, name, initial, x: CX, dir: -1, walking: false, walkT: 0 };
}

/* ============================ rain ============================ */

const WIND = 1.3;
const RAIN_ANGLE = Math.atan(WIND * 0.42);
const RAIN_SLANT_LEN = Math.hypot(WIND * 0.42, 1);
const STREAK_H = 64, STREAK_W = 4;

function createRain(layer) {
  const tex = streakTex();
  const pc = new ParticleContainer({
    dynamicProperties: { position: true, scale: true, rotation: false, color: true },
  });
  layer.addChild(pc);

  const splashLayer = new Container();
  layer.addChild(splashLayer);
  const ring = ringTex();
  const splashPool = [];
  const splashes = [];

  let RW = 1, RH = 1;
  const drops = [];

  function reset(d, init) {
    const z = Math.random();
    d.x = Math.random() * (RW + 180) - 60;
    d.y = init ? Math.random() * RH : -30 - Math.random() * 220;
    d.l = 7 + z * 22;
    d.v = (370 + z * 780) / 60;
    d.w = 0.5 + z * 1.15;
    d.a = 0.11 + z * 0.42;
    const p = d.p;
    p.x = d.x; p.y = d.y;
    p.scaleX = d.w / STREAK_W;
    p.scaleY = (d.l * RAIN_SLANT_LEN) / STREAK_H;
    p.alpha = d.a;
  }

  function size(w, h) {
    RW = w; RH = h;
    const n = Math.max(130, Math.min(260, Math.round(RW * RH / 3300)));
    while (drops.length > n) { const d = drops.pop(); pc.removeParticle(d.p); }
    while (drops.length < n) {
      const p = new Particle({
        texture: tex, anchorX: 0.5, anchorY: 0,
        rotation: RAIN_ANGLE, tint: 0xc6daff,
      });
      pc.addParticle(p);
      const d = { p };
      drops.push(d);
      reset(d, true);
    }
    for (const d of drops) if (d.y > RH) reset(d, true);
    pc.update();
  }

  function step() {
    for (let i = 0; i < drops.length; i++) {
      const d = drops[i];
      d.x -= WIND * d.v * 0.42;
      d.y += d.v;
      if (d.y > RH + 20) {
        if (Math.random() < 0.3) addSplash(d.x, RH - Math.random() * RH * 0.34);
        reset(d, false);
      } else {
        d.p.x = d.x; d.p.y = d.y;
      }
    }
    for (let j = splashes.length - 1; j >= 0; j--) {
      const s = splashes[j];
      s.r += 0.85; s.a -= 0.021;
      if (s.a <= 0) {
        s.sp.visible = false;
        splashPool.push(s.sp);
        splashes.splice(j, 1);
        continue;
      }
      s.sp.width = s.r * 3.4;
      s.sp.height = s.r * 0.9;
      s.sp.alpha = s.a;
    }
  }

  function addSplash(x, y) {
    if (splashes.length > 70) return;
    let sp = splashPool.pop();
    if (!sp) {
      sp = new Sprite(ring);
      sp.anchor.set(0.5);
      sp.tint = 0xe0cca4;
      splashLayer.addChild(sp);
    }
    sp.visible = true;
    sp.position.set(x, y);
    splashes.push({ sp, r: 1, a: 0.32 });
  }

  function setVisible(v) {
    pc.visible = v;
    splashLayer.visible = v;
  }

  return { size, step, setVisible, container: pc };
}

/* ============================ the scene ============================ */

/**
 * @param {HTMLCanvasElement} canvas
 * @param {object} [opts]
 *   opts.me / opts.other  — 'D' | 'Y', which avatar occupies which slot
 *   opts.cacheStatic      — cacheAsTexture the static groups (default true)
 *   opts.rain             — render rain (default true)
 *   opts.overlays         — vignette / scrims / grain (default true)
 *   opts.antialias        — default false (pixijs#10413)
 *   opts.autoStart        — run our own rAF loop (default true)
 * @returns {Promise<object>} the scene controller
 */
export async function createShrineScene(canvas, opts = {}) {
  const o = Object.assign({
    me: 'D', other: 'Y',
    cacheStatic: true, rain: true, overlays: true,
    antialias: false, autoStart: true,
    background: 0x04060a,
  }, opts);

  const renderer = new WebGLRenderer();
  await renderer.init({
    canvas,
    antialias: o.antialias,
    resolution: Math.min(2, globalThis.devicePixelRatio || 1),
    autoDensity: true,
    background: o.background,
    powerPreference: 'high-performance',
    preference: 'webgl',
  });

  const stage = new Container();

  /* --- world tree --------------------------------------------------------
     root       : viewBox transform (scale + offset)
       far      : parallax 0.55x  — sky, warm glow, forest, horizon
       world    : parallax 1.0x   — everything you can walk past
     hud        : screen space    — rain, vignette, scrims, grain          */

  const root = new Container();
  const far = new Container();
  const world = new Container();
  root.addChild(far, world);
  stage.addChild(root);
  const hud = new Container();
  stage.addChild(hud);

  /* ---- far layer ---- */
  far.addChild(gradRect(TEX.sky(), -900, -900, 2400, 2400));

  const warm = new Sprite(TEX.warm());
  warm.x = -130; warm.y = 270; warm.width = 860; warm.height = 580;
  const warmMask = new Graphics().ellipse(300, 560, 430, 290).fill(0xffffff);
  far.addChild(warm, warmMask);
  warm.mask = warmMask;

  const forest = buildForest();
  far.addChild(forest);
  const horizon = new Graphics().rect(-900, 606, 2400, 26)
    .fill({ color: 0x0a0c0b, alpha: 0.92 });
  far.addChild(horizon);

  /* ---- world layer, in the original's paint order ---- */
  const hall = buildHall();
  world.addChild(hall);

  const lamps = new Container();
  const lampA = pivotAt(182, 472);
  const lampAFlick = new Container();
  lampAFlick.addChild(buildLantern(0));
  lampA.inner.addChild(lampAFlick);
  const lampB = pivotAt(418, 472);
  const lampBFlick = new Container();
  lampBFlick.addChild(buildLantern(236));
  lampB.inner.addChild(lampBFlick);
  lamps.addChild(lampA, lampB);
  world.addChild(lamps);

  const torii = new Container();
  torii.addChild(buildTorii(), buildShimenawa());
  world.addChild(torii);

  const ground = buildGround();
  world.addChild(ground.root);

  const bell = buildBell();
  world.addChild(bell.unit);

  const box = buildSaisenBox();
  world.addChild(box);

  const omi = buildOmikuji();
  omi.root.alpha = 0;
  omi.root.y = 170;
  omi.root.visible = false;
  world.addChild(omi.root);

  const rack = buildRack();
  world.addChild(rack);

  // grounds layer sits on top: hut, markers, then the two avatars
  const grounds = new Container();
  world.addChild(grounds);
  const hut = buildHut();
  grounds.addChild(hut.root);
  const marks = buildMarks();
  grounds.addChild(marks.root);

  const avatars = {
    other: buildAvatar(o.other),
    me: buildAvatar(o.me),
  };
  grounds.addChild(avatars.other.root, avatars.me.root);
  avatars.other.root.x = 660;
  avatars.me.root.x = 585;

  /* ---- hud layer ---- */
  const rain = o.rain ? createRain(hud) : null;

  let vig = null, topScrim = null, botScrim = null, grain = null;
  if (o.overlays) {
    // vignette: radial-gradient(120% 82% at 50% 44%, transparent 30%, .5 74%, .88 100%)
    vig = new Sprite(radialGradTex('vig', [
      [0, 0x000000, 0], [0.3, 0x000000, 0], [0.74, 0x000000, 0.5], [1, 0x000000, 0.88],
    ]));
    hud.addChild(vig);

    topScrim = new Sprite(linearGradTex('topScrim', [
      [0, 0x020408, 0.85], [0.45, 0x020408, 0.45], [1, 0x020408, 0],
    ]));
    topScrim.alpha = 0.35;   // #app.started #topScrim
    hud.addChild(topScrim);

    botScrim = new Sprite(linearGradTex('botScrim', [
      [0, 0x020305, 0], [0.58, 0x020305, 0.6], [1, 0x020305, 0.92],
    ]));
    hud.addChild(botScrim);

    grain = new TilingSprite({ texture: noiseTex(128), width: 10, height: 10 });
    grain.alpha = 0.035;
    hud.addChild(grain);
  }

  /* ---- static caching -----------------------------------------------------
     Every group whose *contents* never change is flattened to one texture, so
     a frame costs transforms + a handful of quads. Containers that move as a
     unit (the swinging bell, the bobbing avatars) still cache fine — the
     transform is applied to the cached sprite afterwards.

     The renderer runs antialias:false for the per-frame pass (pixijs#10413),
     but these bake once, so we turn AA on for them and get SVG-grade edges
     on ~90% of the pixels for free.

     IMPORTANT: only containers whose *contents* are frozen belong here. One
     that merely moves/rotates/fades as a whole is fine, but one with an
     independently animating child — the hut's swaying lantern, the omikuji
     stick — must be split so the moving part stays outside the cache.       */
  const cacheables = [
    forest, hall, torii, box, rack,
    hut.back, hut.front,
    omi.back, omi.front, omi.stick,
    bell.suzu.inner, bell.rope.inner,
    ground.blurred, ground.pool,
    avatars.me.body, avatars.other.body,
  ];

  function recache(scale) {
    if (!o.cacheStatic) return;
    const res = Math.max(1, Math.min(3, (renderer.resolution || 1) * scale));
    for (const c of cacheables) {
      if (typeof c.cacheAsTexture !== 'function') continue;
      try {
        c.cacheAsTexture(false);
        c.cacheAsTexture({ resolution: res, antialias: true });
      } catch (e) {
        /* caching is an optimisation, never a correctness requirement */
      }
    }
  }

  /* ============================ layout ============================ */

  let S = 1, VW = SAFE_W, VH = SAFE_H, CW = 1, CH = 1;

  /* We must NOT measure the canvas itself: autoDensity writes an explicit
     pixel width/height onto its inline style, so canvas.clientWidth would
     just echo whatever size we last set. Measure the host box instead. */
  const host = o.resizeTo || canvas.parentElement || document.documentElement;

  function layout() {
    const W = Math.max(1, Math.round(o.width || host.clientWidth || canvas.width || 1));
    const H = Math.max(1, Math.round(o.height || host.clientHeight || canvas.height || 1));
    if (!isFinite(W) || !isFinite(H)) return;
    CW = W; CH = H;
    renderer.resize(W, H);

    S = Math.min(W / SAFE_W, H / SAFE_H);
    if (!S || !isFinite(S)) return;
    VW = W / S; VH = H / S;
    const extra = VH - SAFE_H;
    const vbX = CX - VW / 2;
    const vbY = TOP_Y - extra * 0.22;
    root.scale.set(S);
    root.position.set(-vbX * S, -vbY * S);

    if (rain) rain.size(W, H);
    if (vig) {
      // radial-gradient(120% 82% at 50% 44%): those percentages are RADII,
      // so the sprite is twice each of them.
      vig.width = W * 2.4; vig.height = H * 1.64;
      vig.x = W * 0.5 - vig.width / 2;
      vig.y = H * 0.44 - vig.height / 2;
    }
    if (topScrim) { topScrim.width = W; topScrim.height = H * 0.34; }
    if (botScrim) { botScrim.width = W; botScrim.height = H * 0.21; botScrim.y = H - botScrim.height; }
    if (grain) { grain.width = W; grain.height = H; }

    recache(S);
  }

  /* ============================ state ============================ */

  let camTarget = CX, cam = CX;
  let dim = false, dimBox = 1, dimBell = 1;
  let marksVisible = true, marksAlpha = 1;
  let rackVisible = true, rackAlpha = 1;
  let omiVisible = false, omiAlpha = 0, omiY = 170, stickOut = false, stickY = 158;

  const ripplePool = [];
  const activeRipples = [];

  function spawnRipple(x, y, k, life, style) {
    let g = ripplePool.pop();
    if (!g) { g = new Graphics(); ground.ripples.addChild(g); }
    g.visible = true;
    activeRipples.push({ g, x, y, k, t: 0, life, style });
  }

  let time = 0, lastT = 0, raf = 0, running = false, destroyed = false;
  let ambientRipple = 0.6;

  const PUDDLES = [[150, 884, 118, 20], [474, 830, 96, 15], [300, 1012, 250, 34]];

  function safeRender() {
    if (destroyed) return;
    renderer.render(stage);
  }

  function frame(now) {
    if (destroyed) return;
    raf = requestAnimationFrame(frame);
    const dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 0.016;
    lastT = now;
    tick(dt);
    renderer.render(stage);
  }

  function tick(dt) {
    if (destroyed) return;
    time += dt;

    /* --- camera + parallax (matches the original's lerp exactly) --- */
    cam += (camTarget - cam) * Math.min(1, dt * 3.2);
    const wt = Math.round((CX - cam) * 2) / 2;
    world.x = wt;
    far.x = Math.round(wt * 0.55 * 2) / 2;

    /* --- lantern sway + flick --- */
    const swayA = kf((time % 5.4) / 5.4, [[0, -1.5], [0.5, 1.6], [1, -1.5]]);
    const swayB = kf((((time + 2.1) % 6.7)) / 6.7, [[0, -1.5], [0.5, 1.6], [1, -1.5]]);
    lampA.rotation = swayA * Math.PI / 180;
    lampB.rotation = swayB * Math.PI / 180;
    const FL = [[0, 0.87], [0.22, 1], [0.44, 0.79], [0.61, 0.97], [0.8, 0.84], [1, 0.87]];
    lampAFlick.alpha = kf((time % 3.7) / 3.7, FL);
    lampBFlick.alpha = kf(((time + 1.4) % 4.9) / 4.9, FL);
    hut.sway.rotation = swayB * Math.PI / 180;
    hut.flick.alpha = lampBFlick.alpha;

    /* --- puddle reflections drift --- */
    ground.refl.x = kf((time % 7) / 7, [[0, -4], [0.5, 5], [1, -4]]);

    /* --- marker pulse --- */
    const mp = (time % 2.8) / 2.8;
    const mpS = 0.55 + easeOut(Math.min(1, mp / 0.7)) * (1.65 - 0.55);
    const mpA = mp < 0.7 ? 0.55 * (1 - easeOut(mp / 0.7)) : 0;
    for (const p of marks.pulses) { p.scale.set(mpS); p.alpha = mpA; }

    /* --- dim / visibility transitions --- */
    dimBox += ((dim ? 0.34 : 1) - dimBox) * Math.min(1, dt * 5);
    dimBell += ((dim ? 0.26 : 1) - dimBell) * Math.min(1, dt * 4.3);
    box.alpha = dimBox;
    bell.unit.alpha = dimBell;

    marksAlpha += ((marksVisible ? 1 : 0) - marksAlpha) * Math.min(1, dt * 5);
    marks.root.alpha = marksAlpha;
    marks.root.visible = marksAlpha > 0.01;

    rackAlpha += ((rackVisible ? 1 : 0) - rackAlpha) * Math.min(1, dt * 3.5);
    rack.alpha = rackAlpha;
    rack.visible = rackAlpha > 0.01;

    omiAlpha += ((omiVisible ? 1 : 0) - omiAlpha) * Math.min(1, dt * 4);
    omiY += ((omiVisible ? 0 : 170) - omiY) * Math.min(1, dt * 5);
    omi.root.alpha = omiAlpha;
    omi.root.y = omiY;
    omi.root.visible = omiAlpha > 0.01;
    stickY += ((stickOut ? 0 : 158) - stickY) * Math.min(1, dt * 3.5);
    omi.stick.y = stickY;

    /* --- avatars --- */
    for (const key of ['me', 'other']) {
      const a = avatars[key];
      a.root.x = a.x;
      a.flip.scale.x = a.dir > 0 ? -1 : 1;
      if (a.walking) {
        a.walkT += dt / 0.46;
        // gBob: 0/100% 0, 50% -4px
        a.bob.y = kf(a.walkT % 1, [[0, 0], [0.5, -4], [1, 0]]);
      } else if (a.bob.y !== 0) {
        a.bob.y += (0 - a.bob.y) * Math.min(1, dt * 12);
      }
      if (a.dots.visible) a.dots.alpha = kf((time % 1.6) / 1.6, [[0, 0.28], [0.5, 1], [1, 0.28]]);
    }

    /* --- ambient rain ripples on the puddles --- */
    ambientRipple -= dt;
    if (ambientRipple <= 0) {
      ambientRipple = 0.45 + Math.random() * 0.7;
      const [px, py, prx, pry] = PUDDLES[(Math.random() * PUDDLES.length) | 0];
      spawnRipple(px + (Math.random() * 2 - 1) * prx * 0.8,
        py + (Math.random() * 2 - 1) * pry * 0.6,
        0.35 + Math.random() * 0.35, 0.9, 'rain');
    }

    /* --- ripple pool --- */
    for (let i = activeRipples.length - 1; i >= 0; i--) {
      const r = activeRipples[i];
      r.t += dt / r.life;
      if (r.t >= 1) {
        r.g.clear(); r.g.visible = false;
        ripplePool.push(r.g);
        activeRipples.splice(i, 1);
        continue;
      }
      r.g.clear();
      if (r.style === 'tap') {
        // gtapfx: rx7 ry2.2 stroke 1.2, scale 1 -> 5.5, opacity .7 -> 0
        const k = 1 + easeOut(r.t) * 4.5;
        r.g.position.set(r.x, r.y);
        r.g.scale.set(k);
        r.g.ellipse(0, 0, 7, 2.2).stroke({ color: 0xe8c88a, width: 1.2, alpha: 0.7 * (1 - r.t) });
      } else {
        // addRipple: rx 4 -> 4+95k, ry 1.4 -> 1.4+28k, stroke-opacity .45 -> 0
        r.g.position.set(r.x, r.y);
        r.g.scale.set(1);
        r.g.ellipse(0, 0, 4 + r.t * 95 * r.k, 1.4 + r.t * 28 * r.k)
          .stroke({ color: 0xe8c88a, width: 1.4, alpha: 0.45 * (1 - r.t) });
      }
    }

    if (rain && rain.container.visible) rain.step();
    if (grain) { grain.tilePosition.x = (time * 71) % 128; grain.tilePosition.y = (time * 53) % 128; }
  }

  /* ============================ controller ============================ */

  layout();

  /* Watch the host box rather than `window`: in a Svelte/embedded host the
     container can change size without a window resize ever firing. */
  let ro = null;
  if (o.observeResize !== false && typeof ResizeObserver !== 'undefined') {
    let pending = 0;
    ro = new ResizeObserver(() => {
      if (pending) return;
      pending = requestAnimationFrame(() => { pending = 0; layout(); });
    });
    try { ro.observe(host); } catch (e) { ro = null; }
  }

  function start() {
    if (running || destroyed) return;
    running = true; lastT = 0;
    raf = requestAnimationFrame(frame);
  }
  function stop() {
    running = false;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  }

  if (o.autoStart) start();
  else renderer.render(stage);

  const controller = {
    /* --- required contract --- */

    /** camera x in world coords; the scene eases toward it like the original.
     *  Pass `true` as the 2nd arg to snap. */
    setCamera(x, immediate) {
      camTarget = x;
      if (immediate) cam = x;
    },

    /** who: 'me' | 'other' (or an initial 'D' / 'Y') */
    setAvatar(who, state = {}) {
      if (destroyed) return;
      const a = resolveAvatar(who);
      if (!a) return;
      if (state.x != null) a.x = Math.max(WALK_MIN, Math.min(WALK_MAX, state.x));
      if (state.dir != null) a.dir = state.dir;
      if (state.walking != null) {
        if (state.walking && !a.walking) a.walkT = 0;
        a.walking = !!state.walking;
      }
      if (state.writing != null) {
        a.dots.visible = !!state.writing;
      }
      if (state.visible != null) a.root.visible = !!state.visible;
    },

    /** the gold tap ripple on the ground, in world x */
    ripple(x, y) {
      if (destroyed) return;
      spawnRipple(x, y == null ? 828 : y, 1, 0.55, 'tap');
    },

    setMarkersVisible(v) { marksVisible = !!v; },

    /** the omikuji state: dim the bell + saisen box */
    setDim(v) { dim = !!v; },

    resize() { if (!destroyed) layout(); },

    /** true once destroy() has run; hosts can stop their own loop on it */
    get destroyed() { return destroyed; },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      stop();
      if (ro) { try { ro.disconnect(); } catch (e) { /* noop */ } ro = null; }
      /* Hand every cached RenderTexture back to Pixi's texture pool *before*
         tearing the renderer down. The pool is a process-wide singleton, so
         skipping this leaves dangling entries behind. */
      for (const c of cacheables) {
        try { if (c.isCachedAsTexture) c.cacheAsTexture(false); } catch (e) { /* noop */ }
      }
      try { renderer.render(stage); } catch (e) { /* noop */ }
      try { stage.destroy({ children: true }); } catch (e) { /* noop */ }
      /* NOT destroy(true): that sets releaseGlobalResources, which clears
         Pixi's process-wide TexturePool and CanvasPool and would break any
         other renderer still alive on the page (hot reload, route change). */
      try {
        renderer.destroy({ removeView: false, releaseGlobalResources: false });
      } catch (e) { /* already gone */ }
    },

    /* --- extras (additive; the contract above is what v1/v2 need) --- */

    setRackVisible(v) { rackVisible = !!v; },
    setRainVisible(v) { if (rain) rain.setVisible(!!v); },
    setOmikujiVisible(v) { omiVisible = !!v; if (v) omi.root.visible = true; },
    setStickOut(v) { stickOut = !!v; },
    /** manual driving: call tick(dt) + render() yourself when autoStart:false */
    tick(dt) { tick(dt); },
    render() { safeRender(); },
    start, stop,

    /* --- introspection --- */
    get camera() { return cam; },
    get scale() { return S; },
    get renderer() { return renderer; },
    get stage() { return stage; },
    get world() { return world; },
    /** screen px -> world coords, for hit-testing taps */
    toWorld(clientX, clientY) {
      const r = canvas.getBoundingClientRect();
      const sx = clientX - r.left, sy = clientY - r.top;
      const extra = VH - SAFE_H;
      const vbX = CX - VW / 2, vbY = TOP_Y - extra * 0.22;
      return { x: sx / S + vbX - world.x, y: sy / S + vbY };
    },
    bounds: { WALK_MIN, WALK_MAX, GROUND_Y, CX, SAFE_W, SAFE_H },
  };

  function resolveAvatar(who) {
    if (avatars[who]) return avatars[who];
    const k = String(who || '').toUpperCase();
    for (const key of ['me', 'other']) if (avatars[key].initial === k) return avatars[key];
    return null;
  }

  return controller;
}

export { GROUND_Y, WALK_MIN, WALK_MAX, SAFE_W, SAFE_H, CX };
